import { normalize } from 'normalizr'
import { Schemas } from 'redux-modules/schema'
import { updateEntities } from 'redux-modules/entity'

import api from 'api'

const FETCH_REQUEST = 'realhub/provider/FETCH_REQUEST'
const FETCH_SUCCESS = 'realhub/provider/FETCH_SUCCESS'
const FETCH_FAILURE = 'realhub/provider/FETCH_FAILURE'
const CREATE_REQUEST = 'realhub/provider/CREATE_REQUEST'
const CREATE_SUCCESS = 'realhub/provider/CREATE_SUCCESS'
const CREATE_FAILURE = 'realhub/provider/CREATE_FAILURE'
const UPDATE_REQUEST = 'realhub/provider/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realhub/provider/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realhub/provider/UPDATE_FAILURE'

const SELECT_PROVIDER = 'realhub/provider/SELECT_PROVIDER'

// Initial State
const initialState = {
  creating: false,
  errors: [],
  loaded: false,
  loadedProviderIds: [],
  loading: false,
  result: [],
  selectedId: null,
  updating: false,
}

// Actions
export function fetchRequest(payload = {}){
  return {
    type: FETCH_REQUEST,
    loadedProviderIds: payload.loadedProviderIds,
  }
}

export function fetchSuccess(payload = {}){
  return {
    type: FETCH_SUCCESS,
    result: payload.result,
  }
}

export function fetchFailure(errors = []){
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(){
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(payload = {}){
  return {
    type: CREATE_SUCCESS,
    result: payload.result,
  }
}

export function createFailure(errors = []){
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(){
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(payload = {}){
  return {
    type: UPDATE_SUCCESS,
    result: payload.result,
  }
}

export function updateFailure(errors = []){
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function selectProviderId(providerId){
  return (dispatch, getState) => {
    const state = getState()
    const { providers } = state.entities

    const selectedProvider = providers[providerId]

    dispatch({
      type: SELECT_PROVIDER,
      selectedId: providerId,
      selected: selectedProvider,
    })
  }
}

// Helper Functions
const handleError = errors => errors || ['Something went wrong']

// Action Creators
export function hydrateProvider(provider){
  const normalizedJson = normalize(provider, Schemas.PROVIDER)

  return (dispatch) => {
    dispatch(updateEntities(normalizedJson))
  }
}

export function loadProviders(options){
  return (dispatch) => {
    // Set Loading
    dispatch(fetchRequest())

    const promise = api('/admin/providers.json', options)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.PROVIDER_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess({ result: normalizedJson.result }))

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(fetchFailure(errors))
      })

    return promise
  }
}

export function loadProvider(provider, options){
  return (dispatch, getState) => {
    const { providers } = getState()

    // Set Loading
    const loadedProviderIds = providers.loadedProviderIds ? providers.loadedProviderIds.slice() : []
    if (!loadedProviderIds.includes(provider.id)){
      loadedProviderIds.push(provider.id)
    }
    dispatch(fetchRequest({ loadedProviderIds }))

    const promise = api(`/admin/providers/${provider.id}.json`, options)
      .then(response => (
        response.json().then(json => ({ json, response }))
      ))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        // Add to result array
        const resultArray = getState().providers.result ? getState().providers.result.slice() : []
        if (!resultArray.includes(json.id)){
          resultArray.push(json.id)
        }
        const normalizedJson = normalize(json, Schemas.PROVIDER)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess({ result: resultArray }))
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(fetchFailure(errors))
      })

    return promise
  }
}

export function createProvider(provider, options){
  const config = {
    method: 'POST',
    body: JSON.stringify({
      provider,
    }),
  }

  return (dispatch, getState) => {
    // Set Loading
    dispatch(createRequest())

    const promise = api('/admin/providers.json', options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        // Add to result array
        const resultArray = getState().providers.result ? getState().providers.result.slice() : []
        if (!resultArray.includes(json.id)){
          resultArray.push(json.id)
        }

        const normalizedJson = normalize(json, Schemas.PROVIDER)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess({ result: resultArray }))
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(createFailure(errors))
      })

    return promise
  }
}

export function updateProvider(provider, options){
  const config = {
    method: 'PUT',
    body: JSON.stringify({
      provider,
    }),
  }

  return (dispatch) => {
    // Set Loading
    dispatch(updateRequest())

    const promise = api(`/admin/providers/${provider.id}.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.PROVIDER)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess(normalizedJson))

        return { success: true, result: json }
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))

        return { success: false, result: errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(state = initialState, action = {}){
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loading: true,
        loadedProviderIds: action.loadedProviderIds || state.loadedProviderIds,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        result: action.result,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors,
      }
    case CREATE_REQUEST:
      return { ...state, creating: true }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        result: action.result,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors,
      }
    case UPDATE_REQUEST:
      return { ...state, updating: true }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        result: action.result,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors,
      }
    case SELECT_PROVIDER:
      return {
        ...state,
        selectedId: action.selectedId,
        selected: action.selected,
      }
    default:
      return state
  }
}
